import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import BusinessCard from "@/components/BusinessCard";
import BusinessMap from "@/components/BusinessMap";
import Timeline from "@/components/Timeline";
import { DiscoverySection } from "@/components/DiscoveryCTA";
import { LOGO_WHITE } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, MapPinned, BarChart3, Armchair, HeartPulse, Rocket } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1505843795480-5cfb3c03f6ff?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";
const SELLER_IMG = "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const profiles = [
  { icon: Armchair, title: "Le départ en retraite", text: "Nos cédants sont souvent des chefs d'entreprise qui partent en retraite et cherchent un repreneur de confiance pour transmettre le fruit d'une vie de travail." },
  { icon: HeartPulse, title: "Prendre soin de soi", text: "D'autres souhaitent accorder plus de temps à leur santé et à leurs proches, et préparent sereinement leur sortie de l'activité." },
  { icon: Rocket, title: "De nouveaux projets", text: "Certains désirent simplement sortir de l'activité pour se consacrer à de nouvelles aventures entrepreneuriales ou personnelles." },
];

export default function Landing() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    api.get("/businesses").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  return (
    <div className="bg-[#FDFDFC]">
      <Header />

      {/* Hero */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden">
        <video
          data-testid="hero-video"
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay muted loop playsInline preload="auto" poster={HERO}
        >
          <source src="/hero.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-[#1B2A4A]/80" />
        <div className="relative max-w-4xl mx-auto px-6 sm:px-8 w-full text-center flex flex-col items-center">
          <motion.img
            src={LOGO_WHITE}
            alt="PROAC Transmission"
            data-testid="hero-logo"
            initial={{ opacity: 0, y: -20, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="w-[190px] sm:w-[240px] lg:w-[300px] h-auto object-contain drop-shadow-2xl"
          />
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }} className="max-w-2xl">
            <span className="text-[#E0C173] uppercase tracking-[0.25em] text-xs font-medium">Transmission d'entreprise</span>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-tight tracking-tight">
              Reprendre une entreprise, <span className="text-[#E0C173] italic">en toute sérénité.</span>
            </h1>
            <p className="mt-6 text-white/80 text-base sm:text-lg leading-relaxed mx-auto max-w-xl">
              PROAC Transmission accompagne cédants et repreneurs dans la transmission d'entreprise.
              Découvrez notre catalogue d'affaires à céder, secteur par secteur, région par région.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4 justify-center">
              <Button data-testid="hero-catalogue-btn" onClick={() => navigate("/catalogue")} className="rounded-full bg-[#E0C173] hover:bg-[#C9A85A] text-[#1B2A4A] font-semibold px-8 py-6 text-base">
                Explorer le catalogue <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
              </Button>
              <Button data-testid="hero-register-btn" onClick={() => navigate("/inscription")} variant="outline" className="rounded-full border-white/40 text-white hover:bg-white/10 bg-transparent px-8 py-6 text-base">
                Devenir acheteur
              </Button>
            </div>
            <Link to="/rdv-decouverte" data-testid="hero-rdv-link" className="mt-6 inline-flex items-center gap-2 text-sm text-white/85 hover:text-[#E0C173] transition-colors">
              Vous souhaitez céder votre entreprise ? <span className="text-[#E0C173] font-semibold border-b border-[#E0C173]/60">Demandez votre RDV découverte</span> <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Trust band */}
      <section className="bg-[#0D1629] text-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[["+200", "Entreprises transmises"], ["35 ans", "D'expérience"], ["119 M€", "D'actifs vendus"], ["12", "Départements couverts"]].map(([n, l]) => (
            <div key={l}>
              <div className="font-display text-3xl sm:text-4xl text-[#E0C173]">{n}</div>
              <div className="mt-1 text-xs uppercase tracking-widest text-white/60">{l}</div>
            </div>
          ))}
        </div>
      </section>

      <DiscoverySection />

      {/* Seller profiles - pourquoi céder */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 py-24 overflow-x-hidden">
        <div className="grid lg:grid-cols-2 gap-14 items-center [&>*]:min-w-0">
          <motion.div initial={{ opacity: 0, x: -24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="relative">
            <img src={SELLER_IMG} alt="Chef d'entreprise cédant" className="w-full h-[460px] object-cover rounded-3xl" />
            <div className="absolute -bottom-6 right-2 sm:right-8 bg-[#1B2A4A] text-white rounded-2xl px-7 py-5 shadow-xl">
              <div className="font-display text-3xl text-[#E0C173]">35 ans</div>
              <div className="text-xs uppercase tracking-widest text-white/70">d'expérience à vos côtés</div>
            </div>
          </motion.div>
          <div>
            <span className="text-[#E0C173] uppercase tracking-[0.25em] text-xs font-medium">Qui sont nos cédants ?</span>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl text-[#1B2A4A] tracking-tight">Des dirigeants qui préparent l'avenir</h2>
            <p className="mt-5 text-[#475569] leading-relaxed">
              La plupart de nos cédants sont des <strong className="text-[#1B2A4A] font-semibold">chefs d'entreprise partant en retraite</strong>, à la recherche d'un repreneur capable de poursuivre leur œuvre. Mais chaque histoire est unique.
            </p>
            <div className="mt-9 space-y-5">
              {profiles.map((p, i) => (
                <motion.div
                  key={p.title}
                  data-testid={`seller-profile-${i}`}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="flex items-start gap-4 bg-white border border-gray-200 rounded-2xl p-5 hover:border-[#E0C173]/60 hover:shadow-md transition-all"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#1B2A4A] flex items-center justify-center shrink-0">
                    <p.icon className="w-6 h-6 text-[#E0C173]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-display text-lg text-[#1B2A4A]">{p.title}</h3>
                    <p className="mt-1 text-sm text-[#475569] leading-relaxed">{p.text}</p>
                  </div>
                </motion.div>
              ))}
            </div>
            <Button data-testid="profiles-seller-cta" onClick={() => navigate("/rdv-decouverte")} className="mt-9 rounded-full bg-[#E0C173] hover:bg-[#C9A85A] text-[#1B2A4A] font-semibold px-8 py-4 h-auto whitespace-normal text-left max-w-full">
              Vendre mon entreprise : RDV découverte gratuit <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
      </section>

      {/* Animated timeline */}
      <Timeline />

      {/* Map preview */}
      <section className="bg-white border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-[#E0C173] uppercase tracking-[0.25em] text-xs font-medium">Carte interactive</span>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl text-[#1B2A4A] tracking-tight">Toutes nos affaires, sur une carte</h2>
            <p className="mt-5 text-[#475569] leading-relaxed">
              Visualisez en un coup d'œil les entreprises à céder partout en France.
              Filtrez par secteur d'activité, zone géographique et taux de rentabilité.
            </p>
            <ul className="mt-8 space-y-4">
              {[[MapPinned, "Localisation précise de chaque opportunité"], [BarChart3, "Taux de rentabilité affiché en clair"], [ShieldCheck, "Données détaillées réservées aux acheteurs enregistrés"]].map(([Ic, t]) => (
                <li key={t} className="flex items-start gap-3 text-sm text-[#1B2A4A]">
                  <Ic className="w-5 h-5 text-[#E0C173] mt-0.5 shrink-0" strokeWidth={1.5} /> {t}
                </li>
              ))}
            </ul>
            <Button data-testid="map-section-cta" onClick={() => navigate("/catalogue")} className="mt-9 rounded-full bg-[#1B2A4A] hover:bg-[#0D1629] px-8 py-6">
              Ouvrir la carte <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
            </Button>
          </div>
          <div className="h-[420px] border border-gray-200">
            <BusinessMap items={items} activeId={activeId} onHover={setActiveId} />
          </div>
        </div>
      </section>

      {/* Latest listings */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 py-24">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <span className="text-[#E0C173] uppercase tracking-[0.25em] text-xs font-medium">Sélection</span>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl text-[#1B2A4A] tracking-tight">Dernières entreprises à céder</h2>
          </div>
          <Link to="/catalogue" data-testid="see-all-link" className="text-[#1B2A4A] font-medium text-sm flex items-center gap-1 hover:text-[#E0C173]">
            Voir tout le catalogue <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {items.slice(0, 6).map((b, i) => <BusinessCard key={b.business_id} b={b} index={i} />)}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1B2A4A]">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <h2 className="font-display text-3xl sm:text-4xl text-white tracking-tight">Vous cherchez une entreprise à reprendre&nbsp;?</h2>
          <p className="mt-4 text-white/70 max-w-xl mx-auto">Créez votre profil acheteur pour accéder aux dossiers complets : chiffres, vidéos de présentation et contact direct avec nos conseillers.</p>
          <Button data-testid="cta-register-btn" onClick={() => navigate("/inscription")} className="mt-8 rounded-full bg-[#E0C173] hover:bg-[#C9A85A] text-[#1B2A4A] font-semibold px-10 py-6 text-base">
            Créer mon profil acheteur
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
